/**
 * Authentication Context using Supabase Auth
 *
 * Provides authentication state and methods throughout the application.
 * Uses Supabase for auth operations, syncs with backend for user profile.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, setCachedSession } from '@/lib/supabase';
import type { User, LoginCredentials, RegisterData, AuthContextType } from '@/types/auth';
import { apiClient } from '@/api/client';
import { clearPersistedCache } from '@/lib/queryPersistence';
import { photoUploadQueue } from '@/features/livestock/photoUploadQueue';
import { sortLog } from '@/features/livestock/photoInboxSorting';
import { clearRecents } from '@/features/livestock/photoInboxRecents';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Id of the last account seen on this browser.
 *
 * The React Query persisted cache (`rdh-query-cache`) is a single global entry,
 * not scoped per user, so it must be wiped when the account changes or user B
 * hydrates user A's tanks on first paint. React state cannot answer "who was
 * here last": a full page load (or a fresh tab after A closed theirs) starts
 * with `user === null` while A's cache is still sitting in localStorage. So the
 * id is persisted alongside it and read back at mount.
 */
const LAST_USER_ID_KEY = 'rdh-last-user-id';

function readLastUserId(): string | null {
  try {
    return localStorage.getItem(LAST_USER_ID_KEY);
  } catch {
    // localStorage unavailable (private mode / blocked site data)
    return null;
  }
}

function writeLastUserId(userId: string): void {
  try {
    localStorage.setItem(LAST_USER_ID_KEY, userId);
  } catch {
    // Unavailable — the in-memory ref still guards account switches that
    // happen within this page's lifetime.
  }
}

/**
 * AuthProvider component
 * Wraps the application to provide authentication state and methods
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const initializingRef = useRef<boolean>(false);
  // Mirrors LAST_USER_ID_KEY; seeded from localStorage when the listener is
  // installed so it is correct across a reload boundary.
  const lastUserIdRef = useRef<string | null>(null);

  /**
   * Fetch user profile from backend /auth/me endpoint
   * This syncs Supabase user with our backend user record
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      // Fetch user profile from backend (creates user record if first login)
      // The apiClient will get the session token from the cache
      const response = await apiClient('/api/auth/me', {
        method: 'GET',
      });

      if (response.ok) {
        const userData: User = await response.json();
        setUser(userData);
      } else {
        console.error('Failed to fetch user profile from backend');
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login user with email and password via Supabase
   */
  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    const { error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (error) {
      throw new Error(error.message);
    }

    // Auth state change listener will update session and trigger refreshUser
  }, []);

  /**
   * Register new user via Supabase
   */
  const register = useCallback(async (data: RegisterData): Promise<void> => {
    const { error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          display_name: data.display_name || data.email.split('@')[0],
          timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    // Note: Supabase may require email verification before user can login
  }, []);

  /**
   * Logout user via Supabase
   */
  const logout = useCallback(async (): Promise<void> => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Logout error:', error);
    }
    // Auth state change listener will clear user
    setUser(null);
    setSession(null);
  }, []);

  // Initialize auth state and listen for changes
  useEffect(() => {
    // Prevent double-initialization from React Strict Mode
    if (initializingRef.current) {
      return;
    }
    initializingRef.current = true;

    // Who was signed in last time this browser saw an account? Survives reload.
    lastUserIdRef.current = readLastUserId();

    const rememberUser = (userId: string | undefined | null): void => {
      if (!userId) return;
      lastUserIdRef.current = userId;
      writeLastUserId(userId);
    };

    // Listen for auth state changes - this will fire immediately with INITIAL_SESSION
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        // Update cached session for apiClient to use (avoids getSession() deadlock)
        setCachedSession(newSession);

        if (event === 'INITIAL_SESSION') {
          // Initial load - fetch user profile if we have a session
          if (newSession) {
            // Establish the baseline for the cross-account check below. Never
            // clears here: a restored session IS the cache's owner, and wiping
            // on every page load would defeat persistence entirely.
            rememberUser(newSession.user?.id);
            await refreshUser();
          } else {
            setIsLoading(false);
          }
        } else if (event === 'SIGNED_IN' && newSession) {
          // User just signed in. If this is a DIFFERENT account than the one
          // this browser last saw, the persisted query cache belongs to
          // somebody else — wipe it before the new user's state lands, or
          // user B renders user A's tanks. A same-user re-login or token
          // refresh must NOT clear: that would throw away a warm, valid cache
          // on every sign-in.
          const incomingUserId = newSession.user?.id ?? null;
          if (
            incomingUserId &&
            lastUserIdRef.current &&
            lastUserIdRef.current !== incomingUserId
          ) {
            clearPersistedCache();
            // The photo uploads strip shows the last user's file names and counts,
            // and the inbox the photos they sorted and the organisms they chose.
            photoUploadQueue.clear();
            sortLog.clear();
            clearRecents();
          }
          rememberUser(incomingUserId);
          await refreshUser();
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setIsLoading(false);
          clearPersistedCache();
          // Stops the user's uploads and forgets their file names and counts (shared devices),
          // the photos they sorted and the organisms they chose.
          photoUploadQueue.clear();
          sortLog.clear();
          clearRecents();
        } else if (event === 'TOKEN_REFRESHED') {
          // Session refreshed, user stays logged in
          // No need to re-fetch profile
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      initializingRef.current = false;
    };
  }, [refreshUser]);

  const value: AuthContextType = {
    user,
    setUser,
    isLoading,
    isAuthenticated: user !== null && session !== null,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
